import * as React from "react";

import { useAuth } from "@/providers";
import { useHasOrgRole } from "@/providers/auth/hooks";
import {
  ORG_SESSION_DEFAULTS_KEY,
  type OrgSessionDefaultsConfig,
} from "@/shared/domain/config";
import { useOrgConfig, useUpdateOrgConfig } from "@/shared/domain/config/hooks";
import { MAX_INACTIVITY_MINUTES } from "@/shared/domain/sessions";
import {
  Field,
  FormSection,
  RobotoButton,
  Spinner,
  Switch,
  TextInput,
} from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";

import { SettingsPageLayout } from "../SettingsPageLayout";

import styles from "./SessionSettings.module.css";

const DEFAULT_INACTIVITY_MINUTES = 15;

/**
 * The org's default completion policy: whether Roboto marks a new session
 * complete on its own, and after how long without a new file. Org admins edit it; other
 * members see it.
 */
export function SessionSettings() {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;
  const {
    data: stored,
    isLoading,
    error,
  } = useOrgConfig<OrgSessionDefaultsConfig>(orgId, ORG_SESSION_DEFAULTS_KEY);

  if (isLoading) {
    return <Spinner />;
  }
  if (error || orgId === undefined) {
    return <p className={styles.error}>Could not load the session settings.</p>;
  }
  return (
    <SettingsPageLayout>
      {/* Keyed so a newly selected org's form starts from that org's setting. */}
      <CompletionPolicyForm
        key={orgId}
        orgId={orgId}
        storedMinutes={stored?.completion_policy?.inactivity_minutes ?? null}
      />
    </SettingsPageLayout>
  );
}

function CompletionPolicyForm({
  orgId,
  storedMinutes,
}: {
  orgId: string;
  storedMinutes: number | null;
}) {
  const isAdmin = useHasOrgRole("admin");
  const update = useUpdateOrgConfig();
  const [enabled, setEnabled] = React.useState(storedMinutes !== null);
  const [minutes, setMinutes] = React.useState(
    String(storedMinutes ?? DEFAULT_INACTIVITY_MINUTES),
  );
  const minutesId = React.useId();

  const parsed = Number(minutes);
  const minutesValid =
    Number.isInteger(parsed) && parsed >= 1 && parsed <= MAX_INACTIVITY_MINUTES;
  const changed = enabled
    ? storedMinutes === null || parsed !== storedMinutes
    : storedMinutes !== null;

  const save = () => {
    const value: OrgSessionDefaultsConfig = {
      completion_policy: enabled ? { inactivity_minutes: parsed } : null,
    };
    update.mutate({ orgId, key: ORG_SESSION_DEFAULTS_KEY, value });
  };

  return (
    <FormSection
      title="Completing sessions"
      hint="Whether Roboto marks new sessions complete on its own. Applies to sessions created after you save; existing sessions keep their setting. A session created through the SDK or API can set its own."
    >
      <div className={styles.fields}>
        <Switch
          checked={enabled}
          onCheckedChange={(next) => {
            setEnabled(next);
            update.reset();
          }}
          disabled={!isAdmin}
          label="Complete sessions automatically"
          description="A session is marked complete once no file has been added to it for the time below. Adding a file later puts it back in progress."
        />
        {enabled && (
          <Field.Root invalid={!minutesValid}>
            <Field.Label id={minutesId}>Minutes without a new file</Field.Label>
            <Field.Control
              aria-labelledby={minutesId}
              render={
                <TextInput
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={MAX_INACTIVITY_MINUTES}
                  className={styles.minutes}
                />
              }
              value={minutes}
              onChange={(event) => {
                setMinutes(event.target.value);
                update.reset();
              }}
              disabled={!isAdmin}
            />
            <Field.Error match={!minutesValid}>
              Enter a whole number of minutes from 1 to {MAX_INACTIVITY_MINUTES}{" "}
              (one week).
            </Field.Error>
          </Field.Root>
        )}
        {isAdmin ? (
          <div className={styles.actions}>
            <RobotoButton
              onClick={save}
              disabled={!changed || (enabled && !minutesValid)}
              loading={update.isPending}
            >
              Save
            </RobotoButton>
            {update.isSuccess && <span className={styles.note}>Saved.</span>}
            {update.error && (
              <span className={styles.error} role="alert">
                {userFacingErrorMessage(update.error)}
              </span>
            )}
          </div>
        ) : (
          <p className={styles.note}>Only org admins can change this.</p>
        )}
      </div>
    </FormSection>
  );
}
